<template>
  <div>
    <div class="text-body-m-bold">
      {{ title }}
    </div>
    <div class="mb-2 text-body-s">
      {{ description }}
    </div>
    <VuelidateField :validation="v$.inputValue">
      <template #default="{ error, state }">
        <FieldWrapper :for="id" :error="error">
          <template #default="{ id: fieldId }">
            <InputField
              :id="fieldId"
              v-model="v$.inputValue.$model"
              :aria-label="title"
              :state="state"
              @keydown="handleKeydown"
              @blur="addEmailFromInput"
            >
              <template #prepend>
                <MailIcon class="lucide-icon-s" />
              </template>
              <template #content>
                <ChipBadge
                  v-for="(item, index) in emailAddresses"
                  :key="`${item}-${index}`"
                  class="flex-none"
                  color="purple"
                  size="m"
                  :type="selectedChip === index ? 'solid' : 'default'"
                >
                  {{ item }}
                  <button class="-mr-1 ml-1" @click="removeEmail(index)">
                    <XIcon class="lucide-icon-xs" />
                  </button>
                </ChipBadge>
              </template>
            </InputField>
          </template>
        </FieldWrapper>
      </template>
    </VuelidateField>
  </div>
</template>

<script setup lang="ts">
import { ChipBadge, FieldWrapper, InputField } from '@ramp106/omrjs-core-ui'
import { XIcon, MailIcon } from '@ramp106/omrjs-core-ui/icons'
import { useVuelidate } from '@vuelidate/core'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { uniqueEmailRule } from '@/helpers/validationHelper.ts'
import { email } from '@/services/validators'
import VuelidateField from '@/ui/VuelidateField.vue'

const { t } = useI18n()
const props = defineProps<{
  id: string
  title: string
  description: string
  modelValue: string[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
}>()

const emailAddresses = ref<string[]>([...props.modelValue])
const inputValue = ref('')
const selectedChip = ref<number | null>(null)

const rules = {
  inputValue: {
    email,
    uniqueEmail: uniqueEmailRule(t, { existingEmails: emailAddresses.value }),
  },
}

const v$ = useVuelidate(rules, { inputValue })

const removeEmail = (index: number) => {
  emailAddresses.value.splice(index, 1)
  emit('update:modelValue', emailAddresses.value)
  selectedChip.value = null
}

const addEmailFromInput = () => {
  inputValue.value = inputValue.value.trim()
  if (inputValue.value) {
    v$.value.inputValue.$touch()
    if (!v$.value.inputValue.$error) {
      emailAddresses.value.push(inputValue.value)
      emit('update:modelValue', emailAddresses.value)
      inputValue.value = ''
    }
  }
}

const handleKeydown = (event: Event) => {
  const e = event as KeyboardEvent
  if (e.key === ' ' || e.key === 'Enter') {
    e.preventDefault()
    addEmailFromInput()
  } else if (e.key === 'Backspace') {
    if (!inputValue.value) {
      if (selectedChip.value === null && emailAddresses.value.length > 0) {
        selectedChip.value = emailAddresses.value.length - 1
      } else if (selectedChip.value !== null) {
        removeEmail(selectedChip.value)
      }
    } else {
      selectedChip.value = null
    }
  } else if (e.key === 'Escape') {
    inputValue.value = ''
    selectedChip.value = null
  } else {
    selectedChip.value = null
  }
}

watch(
  () => inputValue.value,
  (newValue) => {
    if (newValue === '') {
      v$.value.inputValue.$reset()
    }
  },
)
</script>
